/**
 * Shared derivations for the guided batch workspace (UX plan §4.4).
 *
 * A "batch" is presentation-layer vocabulary for one AdCreativeBrief plus its
 * generated items (decision #1: batch = brief 1:1, no new entity). Everything
 * here derives display state from the existing schema documents — no writes.
 */

import type {
  AdCreativeBrief,
  AdCreativeItem,
  AdCreativeVideoPlan,
} from '@segmently/schema-models/ad-creative';
import type { AiAssetExperiment } from '@segmently/schema-models/content-plan';
import type { LocaleResolutionMode } from '@modules/shared-locales';
import {
  creativeLocaleHeadline,
  resolveCreativeLocaleProjection,
} from '../../services/creativeLocaleProjection';

/**
 * Angle entries persisted on the brief document (`adAngles`) by the angle
 * generation task. Written by the backend before it was schema-backed, so the
 * brief type does not carry it — this is the read-side contract.
 */
export interface AdCreativeAngle {
  id: string;
  label: string;
  thesis?: string;
  status?: string;
  recommendedCreativeTypeIds?: string[];
  /** Resolvable citation paths grounding the angle (insight-context rework). */
  insightRefs?: string[];
  audienceRef?: { analysisId: string; audienceId: string; labelSnapshot?: string };
}

export type BatchStepKey = 'brief' | 'angles' | 'briefs' | 'creatives' | 'launch';

export const BATCH_STEPS: readonly BatchStepKey[] = ['brief', 'angles', 'briefs', 'creatives', 'launch'];

/** FR8: briefs_first batches carry the draft-generation pointer on the brief. */
export function getCreativeBriefGenerationId(brief: AdCreativeBrief | null | undefined): string | null {
  const raw = (brief as unknown as Record<string, unknown> | null | undefined)?.latestCreativeBriefGenerationId;
  return typeof raw === 'string' && raw ? raw : null;
}

export function readBatchStep(value: string | null): BatchStepKey | null {
  return value && (BATCH_STEPS as readonly string[]).includes(value) ? (value as BatchStepKey) : null;
}

export function getBriefAngles(brief: AdCreativeBrief | null | undefined): AdCreativeAngle[] {
  const raw = (brief as unknown as Record<string, unknown> | null | undefined)?.adAngles;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((angle): angle is Record<string, unknown> => Boolean(angle) && typeof angle === 'object')
    .map((angle) => ({
      id: String(angle.id ?? ''),
      label: String(angle.label ?? angle.id ?? 'Angle'),
      thesis: typeof angle.thesis === 'string' ? angle.thesis : undefined,
      status: typeof angle.status === 'string' ? angle.status : undefined,
      recommendedCreativeTypeIds: Array.isArray(angle.recommendedCreativeTypeIds)
        ? angle.recommendedCreativeTypeIds.map(String)
        : undefined,
      insightRefs: Array.isArray(angle.insightRefs) ? angle.insightRefs.map(String).filter(Boolean) : undefined,
      audienceRef: angle.audienceRef && typeof angle.audienceRef === 'object'
        ? {
          analysisId: String((angle.audienceRef as Record<string, unknown>).analysisId ?? ''),
          audienceId: String((angle.audienceRef as Record<string, unknown>).audienceId ?? ''),
          labelSnapshot: typeof (angle.audienceRef as Record<string, unknown>).labelSnapshot === 'string'
            ? (angle.audienceRef as Record<string, unknown>).labelSnapshot as string
            : undefined,
        }
        : undefined,
    }))
    .filter((angle) => angle.id);
}

/** campaignId is written by the workflow service ahead of the schema — read it defensively. */
export function getBriefCampaignId(brief: AdCreativeBrief | null | undefined): string | undefined {
  const raw = (brief as unknown as Record<string, unknown> | null | undefined)?.campaignId;
  return typeof raw === 'string' && raw ? raw : undefined;
}

export function getSelectedAngleIds(brief: AdCreativeBrief | null | undefined): string[] {
  const raw = (brief as unknown as Record<string, unknown> | null | undefined)?.selectedAngleIds;
  return Array.isArray(raw) ? raw.map(String) : [];
}

export function isWinner(item: AdCreativeItem): boolean {
  return item.review?.approvalStage === 'approved';
}

/** Creative types whose primary deliverable is a video. */
const VIDEO_CREATIVE_TYPES = new Set(['reel', 'single_video', 'story_video', 'in_stream_video']);

export function isVideoFirstItem(item: AdCreativeItem): boolean {
  return VIDEO_CREATIVE_TYPES.has(String(item.creativeType));
}

export function findVideoExperiment(item: AdCreativeItem): AiAssetExperiment | undefined {
  const aiAssets = item.aiAssets as Record<string, {
    selectedExperimentId?: string;
    experiments?: Record<string, AiAssetExperiment>;
  }> | undefined;
  if (!aiAssets) return undefined;
  const slotKey = (item.videoPlan as AdCreativeVideoPlan | undefined)?.slotKey;
  const slots = slotKey && aiAssets[slotKey] ? [aiAssets[slotKey]] : Object.values(aiAssets);
  for (const slot of slots) {
    const selected = slot.selectedExperimentId ? slot.experiments?.[slot.selectedExperimentId] : undefined;
    if (selected?.assetType === 'video') return selected;
    const anyVideo = Object.values(slot.experiments ?? {}).find((experiment) => experiment.assetType === 'video');
    if (anyVideo) return anyVideo;
  }
  return undefined;
}

export function hasCompletedVideo(item: AdCreativeItem): boolean {
  const plan = item.videoPlan as AdCreativeVideoPlan | undefined;
  const experiment = findVideoExperiment(item);
  return plan?.status === 'completed' && Boolean(experiment?.generatedUrl);
}

export function isVideoRunning(item: AdCreativeItem): boolean {
  return (item.videoPlan as AdCreativeVideoPlan | undefined)?.status === 'running';
}

/**
 * Card status pill for the creative grid. `needs_video` (F1) makes explicit
 * that a video-first item only carries its static keyframe/hook image and the
 * actual video has not been generated yet — the item is NOT done.
 */
export type BatchCardStatus =
  | 'ready'
  | 'needs_video'
  | 'video_generating'
  | 'generating'
  | 'failed_validation'
  | 'draft';

export function deriveCardStatus(item: AdCreativeItem): BatchCardStatus {
  if (isVideoFirstItem(item)) {
    if (isVideoRunning(item)) return 'video_generating';
    if (!hasCompletedVideo(item)) return 'needs_video';
  }
  const failedHard = item.validationState?.status === 'failed'
    || (item.validationState?.checks ?? []).some(
      (check) => check.severity === 'error' && check.status === 'fail',
    );
  if (failedHard) return 'failed_validation';
  if (item.status === 'generating_assets') return 'generating';
  if (item.status === 'ready_for_review' || item.status === 'approved' || item.status === 'published') return 'ready';
  return 'draft';
}

export const CARD_STATUS_LABELS: Record<BatchCardStatus, string> = {
  ready: 'Ready',
  needs_video: 'Needs video',
  video_generating: 'Video generating…',
  generating: 'Generating…',
  failed_validation: 'Validation failed',
  draft: 'Draft',
};

export function getItemPreviewImageUrl(
  item: AdCreativeItem,
  locale: string = item.defaultLanguage,
  mode: LocaleResolutionMode = 'preview',
): string | undefined {
  const projection = resolveCreativeLocaleProjection(item, locale, mode);
  return projection.previewMediaKind === 'video' ? undefined : projection.previewUrl ?? undefined;
}

/** Format badge text from the item's format/creative type. */
export function formatBadge(item: AdCreativeItem): string {
  const type = String(item.creativeType);
  if (type === 'reel' || type === 'in_stream_video') return 'Reel';
  if (type === 'story' || type === 'story_video') return 'Story';
  if (type === 'carousel') return 'Carousel';
  if (type === 'single_video') return 'Video';
  const format = String(item.formatId ?? '');
  if (format.includes('story')) return 'Story';
  if (format.includes('reel')) return 'Reel';
  return 'Feed';
}

export function hookLine(
  item: AdCreativeItem,
  locale: string = item.defaultLanguage,
  mode: LocaleResolutionMode = 'preview',
): string {
  return creativeLocaleHeadline(item, locale, mode);
}

export function getItemAngleId(item: AdCreativeItem): string {
  return String((item as unknown as Record<string, unknown>).angleId ?? '');
}

/**
 * Per-creative performance from the funnel-warehouse CAC join (U7/E5):
 * Meta delivery × funnel journeys matched by utm_content/ad id — TRUE
 * conversions per creative, not just CTR.
 */
export interface ItemPerformance {
  spend: number | null;
  impressions: number;
  clicks: number;
  ctr: number | null;
  trialStarts: number;
  purchases: number;
  costPerPurchase: number | null;
}

export function matchPerformanceRows(
  items: AdCreativeItem[],
  rows: Array<Record<string, unknown>>,
): Map<string, ItemPerformance> {
  const byAdId = new Map<string, Record<string, unknown>>();
  for (const row of rows) {
    const adId = String(row.metaAdId ?? '');
    if (adId) byAdId.set(adId, row);
  }
  const result = new Map<string, ItemPerformance>();
  for (const item of items) {
    const adId = String(item.providerAdId ?? '');
    if (!adId) continue;
    const row = byAdId.get(adId);
    if (!row) continue;
    const impressions = Number(row.impressions ?? 0) || 0;
    const clicks = Number(row.clicks ?? 0) || 0;
    result.set(item.id, {
      spend: row.spend == null ? null : Number(row.spend),
      impressions,
      clicks,
      ctr: impressions > 0 ? clicks / impressions : null,
      trialStarts: Number(row.trialStartedJourneys ?? 0) || 0,
      purchases: Number(row.purchaseJourneys ?? 0) || 0,
      costPerPurchase: row.costPerPurchase == null ? null : Number(row.costPerPurchase),
    });
  }
  return result;
}

/** Step rail status line derivations. */
export interface BatchStepStatus {
  key: BatchStepKey;
  label: string;
  statusLine: string;
  complete: boolean;
}

export function deriveStepStatuses(options: {
  brief: AdCreativeBrief | null;
  angles: AdCreativeAngle[];
  items: AdCreativeItem[];
  winnersCount: number;
  launched: boolean;
}): BatchStepStatus[] {
  const { brief, angles, items, winnersCount, launched } = options;
  const readyCount = items.filter((item) => deriveCardStatus(item) === 'ready').length;
  const generatingCount = items.filter((item) => {
    const status = deriveCardStatus(item);
    return status === 'generating' || status === 'video_generating';
  }).length;
  const usedAngleIds = new Set(items.map(getItemAngleId).filter(Boolean));
  const briefsFirst = getCreativeBriefGenerationId(brief);
  return [
    {
      key: 'brief',
      label: 'Brief',
      statusLine: brief ? brief.title : 'Loading…',
      complete: Boolean(brief),
    },
    {
      key: 'angles',
      label: 'Angles',
      statusLine: angles.length
        ? `${usedAngleIds.size || 0} of ${angles.length} used`
        : 'No angles yet',
      complete: angles.length > 0,
    },
    // FR8: review stage — shown only for briefs_first batches.
    ...(briefsFirst
      ? [{
        key: 'briefs' as BatchStepKey,
        label: 'Briefs',
        statusLine: items.length > 0 ? 'Reviewed' : 'Review drafts',
        complete: items.length > 0,
      }]
      : []),
    {
      key: 'creatives',
      label: 'Creatives',
      statusLine: items.length
        ? `${readyCount} ready${generatingCount ? ` · ${generatingCount} generating` : ''}`
        : 'Not generated yet',
      complete: items.length > 0 && readyCount === items.length,
    },
    {
      key: 'launch',
      label: 'Launch',
      statusLine: launched
        ? 'Sent to Meta'
        : winnersCount
          ? `${winnersCount} selected`
          : 'Pick winners first',
      complete: launched,
    },
  ];
}
